// Persistent running-timer chip in the topbar. Polls on mount; other components
// dispatch window 'pms-timer-changed' after start/stop so it refreshes instantly.
import { useCallback, useEffect, useState } from 'react';
import { Square, Timer } from 'lucide-react';
import { timeAPI } from '../lib/api';

export function fmtElapsed(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
}

export default function TimerWidget() {
  const [running, setRunning] = useState(null);
  const [now, setNow] = useState(Date.now());

  const refresh = useCallback(() => {
    timeAPI.running().then(({ data }) => setRunning(data.running));
  }, []);

  useEffect(() => {
    refresh();
    window.addEventListener('pms-timer-changed', refresh);
    return () => window.removeEventListener('pms-timer-changed', refresh);
  }, [refresh]);

  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [running]);

  if (!running) return null;

  const started = new Date(running.started_at).getTime();
  const elapsed = Math.max(0, Math.floor((now - started) / 1000));

  const stop = async () => {
    await timeAPI.stop(running.task_id);
    setRunning(null);
    window.dispatchEvent(new Event('pms-timer-changed'));
  };

  // Nifty-register timer: bold tabular digits over the task name, with a solid
  // accent stop control. The digits are the hero; the chrome stays quiet.
  return (
    <span className="mr-1 inline-flex items-center gap-2.5 rounded-lg border border-pm-border bg-pm-raised py-1 pl-2.5 pr-1 shadow-[var(--pm-elev-1)]">
      <Timer size={14} className="shrink-0 text-pm-accent" aria-hidden />
      <span className="flex min-w-0 flex-col text-left leading-tight">
        <span className="pm-num text-[13px] font-semibold text-pm-text">{fmtElapsed(elapsed)}</span>
        <span className="max-w-36 truncate text-[10px] text-pm-faint">{running.task_title}</span>
      </span>
      <button
        type="button"
        onClick={stop}
        title="Stop timer"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-pm-accent text-pm-accent-ink shadow-[var(--pm-shadow-sm)] hover:brightness-[1.08] active:translate-y-px"
      >
        <Square size={10} fill="currentColor" aria-hidden />
        <span className="sr-only">Stop timer</span>
      </button>
    </span>
  );
}
